import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { SafeDatePipe } from '@app/_v3/pipes/safedate.pipe';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { loadMe } from '@app/store/actions/profile.actions';
import { BookmarkComponent } from '@core/components/bookmark/bookmark.component';
import { PipesModule } from '@core/pipes/pipes.module';
import {
  AccountService,
  BillingV2Service,
  ContextService,
  FilesService,
  SchoolsService,
  UserServicesService,
} from '@core/services';
import { HelpersBffService } from '@core/services/bff/helpers.service';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { RatingComponent } from '@shared/rating/rating.component';
import { getLanguages, getSubjects } from '@store/reducers/data.reducer';
import { getMe } from '@store/reducers/profile.reducer';

import {
  CSchool,
  CSchoolParticipant,
  EUserServiceType,
  IAccountStats,
  ImageSizeEnum,
  SubjectCategoryEnum,
  User,
  UserService,
  UserStatusEnum,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCoverModule } from 'onclass-ui';
import { OnUiCover } from 'onclass-ui/lib/modules/on-ui-cover/on-ui-cover.component';
import { combineLatest, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { i18nService as TranslateService } from '../../../../../i18n/translate.service';

/*
 */
const daysOfWeek = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa', 'su']; // Todo! Move to const global

interface ServiceCardInterface {
  cover: OnUiCover;
  userAvatar: OnUiCover;
  title: string;
  type: EUserServiceType;
  fullTitle: string;
  authorUrl: string[];
  excerpt: string;
  coverImageUrl: string;
  metaImageUrl: string;
  subjectTitle: string;
  subjectIsNative: boolean;
  languageTitle: string;
  category: string;
  discount: number;
  duration: number;
  price_tier: number;
  userpicUrl: string;
  userpicFullname: string;
  authorStats: Partial<IAccountStats>;
  userServiceRoute: string;
  description: string;
  contentDate: Date;
  alertOpened: boolean;
  schoolCover: OnUiCover;
  schoolTitle: string;
  age_from: number;
  age_to: number;
  participants_limit: number;
  meetings_limit: number;
  weekdays: number[];
  date_from: string;
  date_to: string;
}

@Component({
  selector: 'app-user-service',
  standalone: true,
  imports: [
    LocaleSharedModule,
    SafeDatePipe,
    BookmarkComponent,
    CommonModule,
    OnUiCoverModule,
    PipesModule,
    RatingComponent,
  ],
  templateUrl: './user-service.component.html',
  styleUrls: ['./user-service.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserServiceComponent extends ChangableComponent implements OnInit {
  private translate = inject(TranslateService);
  private billingV2Service = inject(BillingV2Service);
  private contextService = inject(ContextService);
  deviceService = inject(DeviceDetectorService);
  private userServicesService = inject(UserServicesService);
  private schoolService = inject(SchoolsService);
  private store = inject(Store);
  private helpers = inject(HelpersBffService);

  @Input() view: string;
  @Input() public set service(service: UserService) {
    this._service = service;
    this.prepareCard();
  }
  public get service() {
    return this._service;
  }
  @Output() public openInModal = new EventEmitter<UserService>();

  @Output() openUnsubscribeModal = new EventEmitter<boolean>();
  @Output() removed = new EventEmitter<boolean>();
  @Output() selected = new EventEmitter<boolean>();

  public eUserServiceType = EUserServiceType;
  public checkoutModalOpen = false;
  public isGuest: boolean;
  public school: CSchool;

  public cardData: ServiceCardInterface = {
    cover: null,
    userAvatar: null,
    title: null,
    type: null,
    fullTitle: null,
    authorUrl: null,
    excerpt: null,
    coverImageUrl: null,
    metaImageUrl: null,
    subjectTitle: null,
    subjectIsNative: null,
    languageTitle: null,
    category: null,
    discount: null,
    duration: null,
    price_tier: null,
    userpicUrl: null,
    userpicFullname: null,
    authorStats: null,
    userServiceRoute: null,
    description: null,
    contentDate: null,
    alertOpened: null,
    schoolCover: null,
    schoolTitle: null,
    age_from: null,
    age_to: null,
    participants_limit: null,
    meetings_limit: null,
    weekdays: null,
    date_from: null,
    date_to: null,
  };
  public debug$ = this.contextService.debug$;
  public doHover = false;

  private _service: UserService;
  private _author: User;
  private _me: User;

  ngOnInit(): void {
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this._me = me;
          this.isGuest = me?.status === UserStatusEnum.guest;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.store.dispatch(loadMe());
        },
      });

    combineLatest([this.store.select(getSubjects), this.store.select(getLanguages)])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([subjects, languages]) => {
          this.cardData.subjectTitle = subjects.find((el) => el.id == this.service.subject_id)?.title;
          this.cardData.languageTitle = languages.all.find((el) => el.id == this.service.language_id)?.title;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private loadSchoolDetails(id: string) {
    if (!id) {
      return;
    }
    this.schoolService
      .getSchoolByIdOrSlug(this._service?.school_id, undefined, 'LOAD_TIME')
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (school) => {
          this.school = school;
          this.cardData.schoolCover = {
            img: this.prepareStorageUrl(FilesService.getFileUrlBySize(school.cover_id, ImageSizeEnum.sm)),
            title: school.title,
            form: 'none',
          };
          this.cardData.schoolTitle = school.title;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public prepareCard() {
    this.cardData.title = this._service?.title;
    this.cardData.type = this._service?.type;
    this.cardData.fullTitle = this.userServicesService.userServiceFullTitle(this._service);
    this.cardData.excerpt = this._service?.excerpt;
    this.cardData.description = this._service?.description;
    if (this._service?.__opts?.cover) {
      this.cardData.coverImageUrl =
        this.prepareStorageUrl(this._service?.__opts?.images[0].widex2) ||
        this.prepareStorageUrl(this._service?.__opts?.images[0].lg);
    } else {
      this.cardData.coverImageUrl =
        this.prepareStorageUrl(
          FilesService.getFileBySize(this._service?.cover, [ImageSizeEnum.wide, ImageSizeEnum.lg])?.url,
        ) || this.prepareStorageUrl(FilesService.getFileUrlBySize(this._service?.cover_id, ImageSizeEnum.wide));
    }
    this.cardData.subjectIsNative = this._service?.subject?.category_id === SubjectCategoryEnum.native_language;
    this.cardData.category = this._service?.category?.title;
    this.cardData.discount = this._service?.discount;
    this.cardData.duration = this._service?.duration;
    this.cardData.price_tier = this._service?.price_tier;
    this.cardData.userServiceRoute = this.helpers.userServiceRouteUniversal(this._service);
    this.cardData.age_from = this._service?.options?.age_from;
    this.cardData.age_to = this._service?.options?.age_to;
    this.cardData.meetings_limit = this._service?.options?.meetings_limit;
    this.cardData.weekdays = this._service?.options?.weekdays;
    this.cardData.participants_limit = this._service?.participants_limit;
    this._author = this._service.author;
    this.cardData.userpicUrl = null;
    if (this._service?.author?.__opts) {
      this.cardData.userpicUrl = this._service?.author?.__opts?.images[0].smx2
        ? this.prepareStorageUrl(this._service?.author?.__opts?.images[0].smx2)
        : FilesService.getFileUrlBySize(this._author.userpic_id, ImageSizeEnum.sm);
    } else {
      this.cardData.userpicUrl = FilesService.getFileUrlBySize(this._author.userpic_id, ImageSizeEnum.sm);
    }

    this.cardData.authorUrl = AccountService.accountRoute(this._author);
    this.cardData.authorStats = this._author.stats;
    this.cardData.userpicFullname = AccountService.getUserEditedName(this._author);
    this.cardData.userAvatar = {
      type: 'avatar',
      img: this.cardData.userpicUrl,
      title: this.cardData.userpicFullname,
      link: (this.cardData.authorUrl || []).join('/'),
      form: 'circle',
    };

    if (this._service.school_id) {
      this.loadSchoolDetails(this._service.school_id);
    }

    this.cardData.cover = {
      img: this.cardData.coverImageUrl,
      title: this.cardData.title,
      link: this.cardData.userServiceRoute,
      aspect: '16 / 9',
    };

    this.cardData.contentDate = this._service ? new Date(this._service.created_at) : new Date();
    this.prepareMetaImage();
    this.detectChanges();
  }

  public get isSingle(): boolean {
    return this.cardData.participants_limit === 1;
  }

  public get isRegularClass(): boolean {
    return this.cardData.type === EUserServiceType.regular;
  }

  public priceWithoutDiscount(amount: number): number {
    return amount > 0 ? +amount / ((100 - this.cardData.discount) / 100) : null;
  }

  public getFormattedAmount(): Observable<string> {
    return this.billingV2Service.formatAmount$(this.cardData.price_tier);
  }

  public get me() {
    return this._me;
  }

  public get author(): User {
    return this._author;
  }

  public get isMe(): boolean {
    return this.me?.id === this._service?.author_id;
  }

  public get visit_info() {
    return this._service?.visit_info || {};
  }

  public formatAge(age: number) {
    return age > 65 ? '65+' : age;
  }

  public weekdaysToString(): string {
    return this.cardData.weekdays
      ?.map((_d) => this.translate.directInstant(`days-of-week.${daysOfWeek[_d]}`))
      .join(', ');
  }

  public typeLabel(type: EUserServiceType): string {
    switch (type) {
      case EUserServiceType.single:
        return 'library.user-service-card.type.single';
      case EUserServiceType.regular:
        return 'library.user-service-card.type.regular';
      case EUserServiceType.mini:
        return 'library.user-service-card.type.mini';
      case EUserServiceType.course:
        return 'library.user-service-card.type.course';
      default:
        return '';
    }
  }

  public onOpenCardInModal() {
    this.openInModal.emit(this._service);
  }

  public participantTrackByFn(index: number, part: CSchoolParticipant): string {
    return part.id;
  }

  public get labelPeriod(): string {
    switch (this._service.type) {
      case EUserServiceType.single:
        return 'library.user-service-card.label-period.single';
      case EUserServiceType.regular:
        return 'library.user-service-card.label-period.regular';
      case EUserServiceType.mini:
        return 'library.user-service-card.label-period.mini';
      case EUserServiceType.course:
        return 'library.user-service-card.label-period.course';
    }
  }

  private prepareMetaImage() {
    this.cardData.metaImageUrl = this._service?.author?.__opts?.images[0].smx2
      ? this.prepareStorageUrl(this._service?.author?.__opts?.images[0].smx2)
      : FilesService.getFileBySize(this._service.cover, [ImageSizeEnum.lg, ImageSizeEnum.md])?.url ||
        FilesService.getFileUrlBySize(this._service.cover_id, ImageSizeEnum.lg);
  }

  public doHoverImage() {
    // this.doHover = !this.doHover;
  }

  public favoriteChanged(event) {
    this._service.visit_info.is_favorite = event;
  }
}
